import React, { useEffect, useContext, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useFlashMessage } from '../context/FlashMessageContext';
import { API_CONFIG } from '../config/api';
import './Login.css';

function LoginSuccess() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { showSuccess, showError } = useFlashMessage();
  const [isVerifying, setIsVerifying] = useState(true);
  const hasProcessedRef = useRef(false);

  const verifyLoginToken = useCallback(async (token) => {
    // Prevent duplicate API calls
    if (!isVerifying) return;
    
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/passwordless/verify-api`, { token });
      
      if (response.data.success) {
        const { token: authToken, user: userData } = response.data;
        
        // Set authentication state
        login(authToken, userData);
        
        // Show success message only once
        showSuccess(`Welcome back, ${userData.fullName}! Redirecting to dashboard...`);
        
        // Navigate to dashboard
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      } else {
        showError('Login verification failed. Please try logging in again.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error) {
      if (error.response) {
        const { data } = error.response;
        if (data.error === 'expired_token') {
          showError('Login link has expired. Please request a new one.');
        } else if (data.error === 'token_used') {
          showError('Login link has already been used. Please request a new one.');
        } else if (data.error === 'invalid_token') {
          showError('Invalid login link. Please request a new one.');
        } else {
          showError('Login verification failed. Please try again.');
        }
      } else {
        showError('Network error. Please check your connection and try again.');
      }
      
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } finally {
      setIsVerifying(false);
    }
  }, [isVerifying, login, showSuccess, showError, navigate]);

  useEffect(() => {
    // Prevent multiple executions using ref
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const userParam = urlParams.get('user');
    
    // Check if this is a redirect from backend (token + user in URL)
    if (urlToken && userParam) {
      // This is a redirect from backend with already verified token and user data
      try {
        const userData = JSON.parse(decodeURIComponent(userParam));
        login(urlToken, userData);
        showSuccess(`Welcome back, ${userData.fullName}! Redirecting to dashboard...`);
        
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
        setIsVerifying(false);
      } catch (error) {
        showError('Login verification failed. Please try logging in again.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        setIsVerifying(false);
      }
    } else if (urlToken) {
      // This is a direct email link with just token - need to verify with backend
      verifyLoginToken(urlToken);
    } else {
      showError('Invalid login link. Please try logging in again.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      setIsVerifying(false);
    }
  }, [login, navigate, showError, showSuccess, verifyLoginToken]); // Include all dependencies

  return (
    <div className="login-container">
      <div className="login-form">
        <h1>🔐 Verifying Login</h1>
        <p>Please wait while we verify your login link...</p>
        {isVerifying && <div className="loading-spinner"></div>}
      </div>
    </div>
  );
}

export default LoginSuccess;
